'use client';

import type React from 'react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { AlertCircle, PlusCircle } from 'lucide-react';
import { Alert, AlertDescription } from 'components/ui/alert';
import { useTranslation } from 'react-i18next';
import { initialInfoSchemaInspection } from './lib/schema';
import firebase from '../../firebase';
import firebaseClass from 'firebase/compat/app';
import { toast } from 'sonner';
import uuid from 'uuid';
import { useAudit } from './audit-context';
import { AuditType } from '../InternalAudit/audit-context';
import { auditCategories, masterTaskList } from '../InternalAuditOverview/mockData/auditData';

export interface InitialInfoInspection {
  title: string;
  category: string;
  subcategory: string;
}

export function AddInspectionTask({
  auditId,
  type,
  tasksLength,
  setCurrentTaskIndex,
}: {
  auditId: string;
  type: AuditType;
  tasksLength: number;
  setCurrentTaskIndex: (index: number) => void;
}) {
  const { audit } = useAudit();

  const isDisabled = audit.state === 'COMPLETED';
  const { t } = useTranslation();

  const [formData, setFormData] = useState<InitialInfoInspection>({
    title: '',
    category: '',
    subcategory: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleChange = (field: keyof InitialInfoInspection, value: string) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };

    // Clear subcategory when category changes
    if (field === 'category') {
      newFormData.subcategory = '';
    }

    setFormData(newFormData);

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  // Get subcategories for the selected category
  const getSubcategoriesForCategory = (categoryName: string) => {
    const tasksForCategory = masterTaskList.filter((task) => task.category.label === categoryName);

    const subcategories: Array<{ id: string; label: string }> = [];
    tasksForCategory.forEach((task) => {
      subcategories.push({ id: task.id, label: task.label });
    });

    return subcategories.sort((a, b) => a.label.localeCompare(b.label, 'da-DK'));
  };

  const validateForm = () => {
    try {
      initialInfoSchemaInspection.parse(formData);

      setOpen(false);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        // Handle Zod validation errors
        if (error.name === 'ZodError') {
          const zodError = error as unknown as { errors: { path: string[]; message: string }[] };
          const newErrors: Record<string, string> = {};

          zodError.errors.forEach((err) => {
            const field = err.path[0] as string;
            newErrors[field] = err.message;
          });

          setErrors(newErrors);
        }
      }
      return false;
    }
  };

  // is it only inspection
  async function addSubtaskToAuditInspection(formData: InitialInfoInspection) {
    // console.log({ formData });
    try {
      if (!auditId) {
        throw new Error(`Missing auditId=${auditId}`);
      }
      const taskId = uuid();
      const subTaskId = uuid();
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`tasksMap.${taskId}`]: {
            id: taskId,
            label: formData.subcategory,
            subTaskIds: firebaseClass.firestore.FieldValue.arrayUnion(subTaskId),
            category: formData.category,
            order: tasksLength,
          },
          [`subTasksMap.${subTaskId}`]: {
            id: subTaskId,
            description: '',
            status: 'not-audited',
            taskId: taskId,
            label: formData.title,
          },
        });
      setCurrentTaskIndex(tasksLength);
      toast.success('Beskrivelse opdateret');
      setFormData({
        title: '',
        category: '',
        subcategory: '',
      });
    } catch (error) {
      toast.error('Fejl ved opdatering af beskrivelse');
      console.error(error);
    }
    // id;
    // '6'(string);
    // label;
    // 'Har medarbejdere den fornødne uddannelse? Tjekkes Site Manager er forpligtet til at afhjælpe afvigelser og observationer inden aftalt deadline. Som udgangspunkt 1 måned efter dato for auditrapport fx kørekort?'(
    //   string
    // );
    // status;
    // 'not-audited'(string);
    // taskId;
    // ('task4');
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      addSubtaskToAuditInspection(formData);

      // update blocks and quantities
    }

    setIsSubmitting(false);
  };

  // Check if there are any validation errors
  const hasErrors = Object.values(errors).some((error) => error !== '');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" disabled={isDisabled}>
          <PlusCircle strokeWidth={1.5} size={18} /> Tilføj inspektions punkt
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Tilføj inspektions punkt</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            {type.includes('inspection') && (
              <div className="space-y-2">
                <Label htmlFor="category" className="flex items-center">
                  Kategori
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="space-y-2">
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleChange('category', value)}
                  >
                    <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Vælg kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {auditCategories
                        .sort((a, b) => a.name.localeCompare(b.name, 'da-DK'))
                        .map((category) => (
                          <SelectItem key={category.id} value={category.name}>
                            {category.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.category && <p className="text-sm text-red-500">{errors.category}</p>}
              </div>
            )}

            {type.includes('inspection') && (
              <div className="space-y-2">
                <Label htmlFor="subcategory" className="flex items-center">
                  Underkategori
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="space-y-2">
                  <Select
                    value={formData.subcategory}
                    onValueChange={(value) => handleChange('subcategory', value)}
                    disabled={!formData.category}
                  >
                    <SelectTrigger className={errors.subcategory ? 'border-red-500' : ''}>
                      <SelectValue
                        placeholder={
                          !formData.category ? 'Vælg kategori først' : 'Vælg underkategori'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getSubcategoriesForCategory(formData.category).map((subcategory) => (
                        <SelectItem key={subcategory.id} value={subcategory.label}>
                          {subcategory.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.subcategory && <p className="text-sm text-red-500">{errors.subcategory}</p>}
              </div>
            )}
            {/* {type.includes('inspection') && (
              <InspectionCascader
                value={formData.category || []}
                onChange={(value) => handleChange('category', value)}
                placeholder="Vælg kategori eller underkategori..."
                className={errors.category ? 'border-red-500' : ''}
              />
            )} */}
          </div>
          <div className="space-y-2">
            <Label htmlFor="title" className="flex items-center">
              Titel
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Indtast titel på inspektionspunkt"
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Tilføjer...' : 'Tilføj'}
            </Button>
          </DialogFooter>
        </form>
        {hasErrors && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Ret venligst fejlene nedenfor, før du fortsætter.</AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
